import { createApp, h } from 'vue'
import resetCss from '@styles/reset.css?inline'

/**
 * Mount an embeddable Vue widget into a Shadow DOM attached to a
 * host page element.
 *
 * Conventions:
 *   - Host element selector defaults to `#imobile-<widgetName>` so
 *     the embedder only has to drop a div with that id and the
 *     widget finds itself.
 *   - Embedder config travels on `data-*` attributes of the host
 *     element (data-api-base, data-on-success, data-no-shadow, ...).
 *     Anything more involved than a few strings should live in the
 *     widget's own UI, not in attributes.
 *   - Shadow DOM is on by default. Embedders who want to override
 *     widget styles from their own stylesheet can opt out with
 *     `data-no-shadow="true"`.
 *
 * @param {Object} opts
 * @param {string} opts.widgetName  e.g. "special-order" — used for the
 *   default selector and for logs.
 * @param {import('vue').Component} opts.component  Root Vue component.
 * @param {string} opts.styles  Widget-specific CSS, imported with
 *   ?inline so it's a literal string at bundle time.
 * @param {string} [opts.selector]  Override the default selector
 *   (#imobile-<widgetName>). Rarely needed.
 */
export function mountWidget({ widgetName, component, styles, selector }) {
    const sel = selector || `#imobile-${widgetName}`
    const host = document.querySelector(sel)
    if (!host) {
        // Loud warning rather than silent no-op — almost always means
        // the embedder put the script tag before the mount div, or
        // typo'd the id.
        console.warn(
            `[imobile-widget:${widgetName}] mount element ${sel} not found — widget will not render. ` +
            `Make sure the script tag appears AFTER the mount div in the HTML.`
        )
        return null
    }

    // Read config off data-* attributes. apiBaseUrl falls back to the
    // build-time default baked in via VITE_API_BASE_URL; callers
    // explicitly setting data-api-base override that per-embed.
    const config = {
        apiBaseUrl:
            host.dataset.apiBase ||
            import.meta.env.VITE_API_BASE_URL ||
            '',
        // Name of a global function on `window` to call after a
        // successful submit. Strings rather than function refs because
        // data-* attributes can only carry strings.
        onSuccessGlobal: host.dataset.onSuccess || null,
        widgetName,
    }

    // Shadow DOM by default. Embedders who want full control of the
    // widget's look (override our CSS from their own stylesheet) can
    // opt out with data-no-shadow="true".
    const useShadow = host.dataset.noShadow !== 'true'
    let mountTarget
    if (useShadow) {
        // `open` mode so DevTools can still inspect the shadow root
        // — `closed` would make debugging in a customer's page much
        // harder for no real security benefit (anyone running JS in
        // the page can already do anything).
        const shadow = host.attachShadow({ mode: 'open' })
        // Inject reset + widget styles into the shadow root. Order
        // matters: reset first, widget rules second so they can
        // override.
        const styleEl = document.createElement('style')
        styleEl.textContent = `${resetCss}\n${styles || ''}`
        shadow.appendChild(styleEl)
        // Vue mounts into a regular element, not into the shadow
        // root itself — gives us a single root we can `unmount()`
        // later if we want.
        mountTarget = document.createElement('div')
        shadow.appendChild(mountTarget)
    } else {
        // No-shadow mode: inject the styles into <head> so they
        // apply to the widget but explicitly leak into the host
        // page (this is the embedder's opt-in).
        if (styles && !document.querySelector(`style[data-imobile-widget="${widgetName}"]`)) {
            const styleEl = document.createElement('style')
            styleEl.dataset.imobileWidget = widgetName
            styleEl.textContent = styles
            document.head.appendChild(styleEl)
        }
        mountTarget = host
    }

    const app = createApp({
        render: () => h(component, { config }),
    })
    app.mount(mountTarget)
    return app
}
